import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { IconButton, Text } from 'react-native-paper';

import { CloseIcon } from '@/assets';
import { ReadMoreText } from '@/features/start/components/components/ReadMoreText';
import { SectionContent } from '@/features/start/components/components/SectionContent';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';
import type { SVGComponent } from '@/types';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    title: {
      gap: 4,
      paddingBottom: 8,
      flexDirection: 'row',
      alignItems: 'center'
    },
    iconButton: { margin: 0, marginTop: -8, marginRight: -8 },
    more: { color: theme.colors.primary, fontWeight: 'bold' }
  });

type Props = {
  icon: SVGComponent;
  title: string;
  testId?: string;
  text: string;
  hasMore?: boolean;
  maxLines?: number;
  onMore?: () => void;
  onClose?: () => void;
};

export const ClosableCard: FC<Props> = ({
  icon,
  title,
  testId,
  text,
  hasMore = false,
  maxLines,
  onMore,
  onClose
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const { t } = useTranslation(LocalizationNameSpace.START, {
    keyPrefix: 'components'
  });

  return (
    <SectionContent
      title={title}
      testId={testId}
      titleVariant="headlineSmall"
      icon={icon}
      end={
        onClose ? (
          <IconButton
            style={styles.iconButton}
            size={24}
            icon={({ size, color }) => (
              <CloseIcon fill={color} width={size} height={size} />
            )}
            onPress={onClose}
          />
        ) : undefined
      }
    >
      {maxLines == undefined ? (
        <Text onPress={onMore}>
          <Text variant="bodyLarge">{text}</Text>
          {hasMore && (
            <Text variant="bodyLarge" style={styles.more}>
              {t('more')}
              {'...'}
            </Text>
          )}
        </Text>
      ) : (
        <ReadMoreText maxLines={maxLines} text={text} onMore={onMore} />
      )}
    </SectionContent>
  );
};

ClosableCard.displayName = 'ClosableCard';
